import { ArrowLeft, KeyRound, LogIn } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useEmailToken } from "../hooks/useEmailToken.js";

import { Field } from "../components/Field.js";
import { Action, ErrorLine, LinkAction, Screen } from "../components/Screen.js";
import { resetPassword } from "../services/authService.js";

/** Mirrors `password::MIN_LENGTH`. Never stricter than the server. */
const MIN_PASSWORD = 10;

/**
 * Set a new password, with the token from the reset email.
 *
 * **Every session ends, including this one.** `src/http/auth` signs out every
 * device on a successful reset — which is the point of a reset — so this screen
 * routes to sign-in afterwards rather than assuming anything survived. Sending
 * somebody to a page that then bounces them for being unauthenticated is how a
 * working reset reads as a broken one.
 */
export function ResetPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useEmailToken();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <Screen
        title={t("reset.incomplete.title")}
        lead={t("reset.incomplete.lead")}
        footer={
          <LinkAction to="/forgot" icon={KeyRound} accent>
            {t("reset.incomplete.action")}
          </LinkAction>
        }
      >
        {null}
      </Screen>
    );
  }

  if (done) {
    return (
      <Screen
        title={t("reset.done.title")}
        lead={t("reset.done.lead")}
        footer={
          <LinkAction to="/login" icon={LogIn} accent>
            {t("login.title")}
          </LinkAction>
        }
      >
        {null}
      </Screen>
    );
  }

  return (
    <Screen
      title={t("reset.title")}
      footer={
        <LinkAction to="/login" icon={ArrowLeft}>
          {t("common.backToSignIn")}
        </LinkAction>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void (async () => {
            try {
              await resetPassword(token, password);
              setDone(true);
              // Nothing is signed in any more, so the front door is the only
              // honest place to be next.
              setTimeout(() => {
                void navigate("/login");
              }, 2500);
            } catch (cause) {
              setError(cause instanceof Error ? cause.message : t("reset.failed"));
              setBusy(false);
            }
          })();
        }}
        className="flex flex-col gap-4"
      >
        <Field
          id="password"
          label={t("reset.newLabel")}
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          required
          hint={t("reset.newHint", { min: MIN_PASSWORD })}
        />
        {error && <ErrorLine>{error}</ErrorLine>}
        <Action disabled={busy || [...password].length < MIN_PASSWORD}>
          {t("reset.action")}
        </Action>
      </form>
    </Screen>
  );
}
